"use client";

import { Database } from "@punchup/data/supabase.types";
import { ShowWithComedian } from "@/data/shows";
import { createClientComponentClient } from "@punchup/data/supabase/client";
import { genAbsoluteURL } from "@/components/common/utils";
import { getUserZipcodeOrFallbackToIp } from "@/data/zipcode";
import { useQuery } from "@tanstack/react-query";

const RADIUS = 100; // miles

interface UseNearbyShowsOptions {
  comedianId: string;
}

interface UserLocation {
  zipcode: string | null;
  countryCode: string | null;
}

/**
 * Fetches user's location (zipcode and country code)
 * from their profile or falls back to IP-based location
 */
const fetchUserLocation = async (): Promise<UserLocation> => {
  const supabase = createClientComponentClient<Database>();
  const { zipcode, country_code } =
    await getUserZipcodeOrFallbackToIp(supabase);

  return {
    zipcode: zipcode || null,
    countryCode: country_code || null,
  };
};

/**
 * Fetches shows near the user for a specific comedian
 */
const fetchNearbyShows = async (
  comedianId: string,
  zipcode: string,
  countryCode: string
): Promise<ShowWithComedian[]> => {
  // Intentionally omit startDatetime so the /api/shows route falls back to its
  // default 12h-after-start grace cutoff (same as the "All shows" listing).
  // Sending an exact UTC instant here dropped nearby shows hours before
  // showtime — see TIC-535.
  const response = await fetch(
    genAbsoluteURL(
      `/api/shows?postalCode=${zipcode}&countryCode=${countryCode}&radius=${RADIUS}&comedianId=${comedianId}`
    )
  );

  if (!response.ok) {
    throw new Error("Failed to fetch nearby shows");
  }

  return response.json();
};

/**
 * Custom hook for checking if a comedian has shows near the user
 * Uses React Query for caching and automatic refetching
 *
 * @param comedianId - The comedian's ID to check for nearby shows
 * @returns Object containing nearYou boolean, loading state, and location data
 */
export function useNearbyShows({ comedianId }: UseNearbyShowsOptions) {
  // First query: Get user location
  const {
    data: location,
    isLoading: isLoadingLocation,
    error: locationError,
  } = useQuery<UserLocation>({
    queryKey: ["userLocation"],
    queryFn: fetchUserLocation,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  // Second query: Get nearby shows (only runs if we have location and valid comedian ID)
  const {
    data: shows,
    isLoading: isLoadingShows,
    error: showsError,
  } = useQuery<ShowWithComedian[]>({
    queryKey: [
      "nearbyShows",
      comedianId,
      location?.zipcode,
      location?.countryCode,
    ],
    queryFn: () =>
      fetchNearbyShows(comedianId, location!.zipcode!, location!.countryCode!),
    enabled: !!location?.zipcode && !!location?.countryCode && !!comedianId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });

  const isLoading = isLoadingLocation || isLoadingShows;
  const error = locationError || showsError;

  return {
    isLoading,
    error,
    shows: shows || [],
    zipcode: location?.zipcode || null,
    countryCode: location?.countryCode || null,
  };
}
